import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Field, { FieldProps } from '~/app/modules/form/Field';
import classes from './style.module.scss';
import useField from '~/app/modules/form/useField';
import Icon from '~/app/common/Icon';
import Input from '~/app/modules/form/Search/Input';
import InfiniteScroll from 'react-infinite-scroll-component';
import { SearchMachine } from '~/types';
import useTranslation from '~/app/hooks/useTranslation';
import { formatMachineSearchLabel } from '~/machines/helpers';
import Axios from '~/axiosWrapper';
import { API } from '~/app/routes';

export interface MachineMultiSelectPropsDefault extends React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement> {
  defaultOptions: SearchMachine[],
  optionChange: (selected: SearchMachine[]) => void
  position?: 'top' | 'bottom'
}

export type SelectProps = MachineMultiSelectPropsDefault & FieldProps

export type MachineMultiSelectComponent = React.RefForwardingComponent<HTMLSelectElement, SelectProps>

const PAGE_SIZE = 20;

const formatMachineLabel = (machine: SearchMachine) => `${machine.intern} ${machine.name}`;

const matchesSearch = (machine: SearchMachine, query: string): boolean => {
  if (!query) {
    return true;
  }
  const haystack = [
    machine.intern,
    machine.name,
    machine.assignedDriverName,
  ].map(value => String(value ?? '').toLowerCase()).join(' ');
  return haystack.includes(query);
};

const MachineMultiSelect: MachineMultiSelectComponent = (props, ref) => {

  const [selectedOptions, setSelectedOptions] = useState<SearchMachine[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [allMachines, setAllMachines] = useState<SearchMachine[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [fetching, setFetching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const loadedRef = useRef(false);

  const { t } = useTranslation('training');

  const initialized = useRef<boolean>(false);

  const {
    fieldProps: { iconPosition = 'right', iconSize = 32, ...fieldProps },
    inputProps: { defaultOptions, optionChange, position = 'bottom'}
  } = useField<MachineMultiSelectPropsDefault>(props);

  const selectedIds = useMemo(
    () => new Set(selectedOptions.map(m => m.id)),
    [selectedOptions]
  );

  const filteredMachines = useMemo(() => {
    const query = searchText.toLowerCase().trim();
    return allMachines
      .filter(m => !selectedIds.has(m.id))
      .filter(m => matchesSearch(m, query));
  }, [allMachines, selectedIds, searchText]);

  const availableMachines = useMemo(
    () => filteredMachines.slice(0, visibleCount),
    [filteredMachines, visibleCount]
  );

  const toggleDropdown = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleAdd = useCallback((machine: SearchMachine) => {
    setSelectedOptions(prev => {
      if (prev.some(v => v.id === machine.id)) {
        return prev;
      }
      return [...prev, machine];
    });
    setSearchText('');
    setIsOpen(false);
  }, []);

  const handleRemove = useCallback((e: React.MouseEvent<HTMLButtonElement>, machine: SearchMachine) => {
    e.stopPropagation();
    setSelectedOptions(prev => prev.filter(v => v.id !== machine.id));
  }, []);

  const searchMachine = useCallback((text: string) => {
    setSearchText(text);
    setVisibleCount(PAGE_SIZE);
  }, []);

  const fetchNext = useCallback(() => {
    setVisibleCount(prev => prev + PAGE_SIZE);
  }, []);

  const loadMachines = useCallback(() => {
    if (loadedRef.current || fetching) {
      return;
    }
    setFetching(true);
    Axios.get<SearchMachine[], SearchMachine[]>(API.machines)
      .then((data) => {
        setAllMachines(Array.isArray(data) ? data : []);
        loadedRef.current = true;
      })
      .finally(() => {
        setFetching(false);
      });
  }, [fetching]);

  useEffect(() => {
    setSelectedOptions(defaultOptions);
  }, [defaultOptions]);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }
    optionChange(selectedOptions);
  }, [optionChange, selectedOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const currentRef = dropdownRef?.current as HTMLElement | null;
      if (currentRef && !currentRef.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside, { passive: true });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      loadMachines();
      setVisibleCount(PAGE_SIZE);
    }
  }, [isOpen, loadMachines]);

  return (
    <Field
      iconPosition={iconPosition}
      iconSize={iconSize}
      {...fieldProps}
    >
      <div ref={dropdownRef} className={classes['custom-dropdown']}>
        {selectedOptions.length > 0 && (
          <ul className={classes['custom-dropdown__selected-list']}>
            {selectedOptions.map((machine) => (
              <li key={machine.id} className={classes['custom-dropdown__selected-row']}>
                <span className={classes['custom-dropdown__selected-label']}>
                  {formatMachineLabel(machine)}
                </span>
                {!machine.isDeleteRestricted && <button
                  type="button"
                  className={classes['custom-dropdown__selected-remove']}
                  onClick={e => handleRemove(e, machine)}
                  aria-label="Remove"
                >
                  <Icon name="delete_outline" size={20} />
                </button>}
              </li>
            ))}
          </ul>
        )}

        <div className={classes['custom-dropdown__add-wrap']}>
          <button
            type="button"
            className={classes['custom-dropdown__add-button']}
            onClick={toggleDropdown}
            aria-expanded={isOpen}
          >
            <Icon name="add" size={20} />
            <span>{t('addMachine')}</span>
          </button>

          {isOpen && (
            <div
              id="search-scroll-wrapper"
              className={`${classes['custom-dropdown__menu']} ${classes[position]}`}
            >
              <div className={classes['custom-dropdown__search-container']}>
                <Input
                  autoCorrect="off"
                  placeholder={t('searchForMachine')}
                  value={searchText}
                  onSearch={searchMachine}
                  fetching={fetching}
                  className={classes['custom-dropdown__search']}
                />
              </div>

              {availableMachines.length > 0 || fetching ? (
                <InfiniteScroll
                  dataLength={availableMachines.length}
                  next={fetchNext}
                  hasMore={availableMachines.length < filteredMachines.length}
                  loader={<div />}
                  scrollableTarget="search-scroll-wrapper"
                >
                  {availableMachines.map((machine) => (
                    <div
                      key={machine.id}
                      className={classes['custom-dropdown__result-row']}
                      onClick={() => handleAdd(machine)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAdd(machine);
                      }}
                    >
                      <Icon name="add" size={18} className={classes['custom-dropdown__result-icon']} />
                      {formatMachineSearchLabel(machine)}
                    </div>
                  ))}
                </InfiniteScroll>
              ) : (
                <div className={classes['custom-dropdown__no-results']}>{t('noMachinesFound')}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </Field>
  );
};

export default memo(React.forwardRef(MachineMultiSelect));